import React, { useState } from 'react';
import {
    Box,
    Button,
    Typography,
    TextField,
    Stepper,
    Step,
    StepLabel,
    Avatar,
    IconButton,
    Fade,
    Zoom,
    Slide,
    Grow,
    CircularProgress,
    Divider,
    useTheme,
    Checkbox,
    FormControlLabel,
    Alert,
    Modal,
    Paper,
    Container,
    useMediaQuery
} from '@mui/material';
import { useAuth } from '../auth/AuthContext';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { keyframes } from '@mui/system';
import { useTranslation } from '../admin/hooks/useTranslation';

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-20px); }
  60% { transform: translateY(-10px); }
`;

const stepKeys = [
  'register.personelInfo',
  'register.AccountDetails',
  'register.Confirmation',
];

interface RegisterSystemProps {
    mode?: 'page' | 'modal';
    open?: boolean;
    onClose?: () => void;
}

const RegisterSystem: React.FC<RegisterSystemProps> = ({
    mode = 'page',
    open = true,
    onClose = () => { }
}) => {
    const { t } = useTranslation();
    const steps = stepKeys.map((key, idx) =>
    t(key, ['Personal Info', 'Account Details', 'Confirmation'][idx])
  );
    const { register } = useAuth();
    const [activeStep, setActiveStep] = useState(0);
    const [form, setForm] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
        terms: false
    });
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const validateStep = (step: number) => {
        const newErrors: Record<string, string> = {};

        if (step === 0) {
            if (!form.firstName.trim()) newErrors.firstName = t('register.firstNameRequired', 'First name is required');
            if (!form.lastName.trim()) newErrors.lastName = t('register.lastNameRequired', 'Last name is required');
        } else if (step === 1) {
            if (!form.email.trim()) {
                newErrors.email = t('register.emailRequired', 'Email is required');
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
                newErrors.email = t('register.invalidEmail', 'Invalid email format');
            }

            if (!form.password) {
                newErrors.password = t('register.passwordRequired', 'Password is required');
            } else if (form.password.length < 8) {
                newErrors.password = t('register.passwordLength', 'Password must be at least 8 characters');
            }

            if (form.password !== form.confirmPassword) {
                newErrors.confirmPassword = t('register.passwordMismatch', 'Passwords do not match');
            }
        } else if (step === 2) {
            if (!form.terms) {
                newErrors.terms = t('register.termsRequired', 'You must accept the terms');
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNext = () => {
        if (validateStep(activeStep)) {
            setActiveStep(prev => prev + 1);
        }
    };

    const handleBack = () => {
        setActiveStep(prev => prev - 1);
    };

    const handleSubmit = async () => {
        if (!validateStep(2)) return;

        setLoading(true);
        try {
            await register(form.firstName, form.lastName, form.email, form.password);
            setSuccess(true);
        } catch (err: any) {
            setErrors({
                submit: err.response?.data?.message || t('register.registrationFailed', 'Registration failed. Please try again.')
            });
        } finally {
            setLoading(false);
        }
    };

    const renderStepContent = (step: number) => {
        switch (step) {
            case 0:
                return (
                    <Fade in={true} timeout={500}>
                        <Box sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                                {t('register.personalInfoTitle', 'Tell us about yourself')}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                <TextField
                                    fullWidth
                                    label={t('register.firstName', 'First Name')}
                                    name="firstName"
                                    value={form.firstName}
                                    onChange={handleChange}
                                    error={!!errors.firstName}
                                    helperText={errors.firstName}
                                />
                                <TextField
                                    fullWidth
                                    label={t('register.lastName', 'Last Name')}
                                    name="lastName"
                                    value={form.lastName}
                                    onChange={handleChange}
                                    error={!!errors.lastName}
                                    helperText={errors.lastName}
                                />
                            </Box>
                            <Typography variant="body2" color="textSecondary">
                                {t('register.personalInfoDescription', "We'll use this information to personalize your experience.")}
                            </Typography>
                        </Box>
                    </Fade>
                );
            case 1:
                return (
                    <Zoom in={true} timeout={500}>
                        <Box sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                                {t('register.accountDetailsTitle', 'Create your account')}
                            </Typography>
                            <TextField
                                fullWidth
                                label={t('register.email', 'Email')}
                                name="email"
                                type="email"
                                value={form.email}
                                onChange={handleChange}
                                error={!!errors.email}
                                helperText={errors.email}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                fullWidth
                                label={t('register.password', 'Password')}
                                name="password"
                                type="password"
                                value={form.password}
                                onChange={handleChange}
                                error={!!errors.password}
                                helperText={errors.password}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                fullWidth
                                label={t('register.confirmPassword', 'Confirm Password')}
                                name="confirmPassword"
                                type="password"
                                value={form.confirmPassword}
                                onChange={handleChange}
                                error={!!errors.confirmPassword}
                                helperText={errors.confirmPassword}
                            />
                        </Box>
                    </Zoom>
                );
            case 2:
                return (
                    <Slide direction="up" in={true} timeout={500}>
                        <Box sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                                {t('register.confirmationTitle', 'Almost done!')}
                            </Typography>
                            <Box sx={{ mb: 3 }}>
                                <Typography>
                                    <strong>{t('register.nameLabel', 'Name:')}</strong> {form.firstName} {form.lastName}
                                </Typography>
                                <Typography>
                                    <strong>{t('register.emailLabel', 'Email:')}</strong> {form.email}
                                </Typography>
                            </Box>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name="terms"
                                        checked={form.terms}
                                        onChange={handleChange}
                                        color="primary"
                                    />
                                }
                                label={
                                    <Typography variant="body2">
                                        {t('register.termsAgreement', 'I agree to the Terms of Service and Privacy Policy')}
                                    </Typography>
                                }
                            />
                            {errors.terms && (
                                <Typography color="error" variant="body2">
                                    {errors.terms}
                                </Typography>
                            )}
                            {errors.submit && (
                                <Alert severity="error" sx={{ mt: 2 }}>
                                    {errors.submit}
                                </Alert>
                            )}
                        </Box>
                    </Slide>
                );
            default:
                return null;
        }
    };

    const renderSuccessContent = () => (
        <Grow in={true} timeout={500}>
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <CheckCircleIcon
                    color="success"
                    sx={{ fontSize: 80, mb: 2, animation: `${pulse} 2s infinite` }}
                />
                <Typography variant="h5" gutterBottom>
                    {t('register.registrationSuccessful', 'Registration Successful!')}
                </Typography>
                <Typography sx={{ mb: 3 }}>
                    {t('register.verificationEmailSent', "We've sent a verification email to")} <strong>{form.email}</strong>.
                    {t('register.checkInbox', 'Please check your inbox to complete registration.')}
                </Typography>
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            if (mode === 'modal') {
                                onClose();
                            }
                            navigate('/verify-email');
                        }}
                        sx={{ px: 3 }}
                    >
                        {t('register.verifyEmail', 'Verify Email')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            if (mode === 'modal') {
                                onClose();
                            }
                            navigate('/');
                        }}
                        sx={{ px: 3 }}
                    >
                        {t('register.goToHomepage', 'Go to Homepage')}
                    </Button>
                </Box>
            </Box>
        </Grow>
    );

    const renderFormActions = () => {
        if (success) return null;

        return (
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
                <Button
                    disabled={activeStep === 0}
                    onClick={handleBack}
                    variant="outlined"
                >
                    {t('register.back', 'Back')}
                </Button>
                {activeStep === steps.length - 1 ? (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={24} /> : t('register.completeRegistration', 'Complete Registration')}
                    </Button>
                ) : (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleNext}
                    >
                        {t('register.next', 'Next')}
                    </Button>
                )}
            </Box>
        );
    };

    const registerContent = (
        <Box sx={{ position: 'relative' }}>
            {mode === 'modal' && (
                <IconButton
                    sx={{ position: 'absolute', right: 8, top: 8, zIndex: 1 }}
                    onClick={onClose}
                >
                    <CloseIcon />
                </IconButton>
            )}

            <Box sx={{ p: isMobile ? 1 : 3 }}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Avatar
                        src="/logo192.png"
                        sx={{
                            width: 60,
                            height: 60,
                            mx: 'auto',
                            mb: 2,
                            animation: `${bounce} 2s infinite`
                        }}
                    />
                    <Typography variant="h5" component="h1">
                        {t('register.joinCommunity', 'Join Our Community')}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {t('register.createAccountSteps', 'Create your account in just a few steps')}
                    </Typography>
                </Box>

                <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{t(`register.steps.${label.toLowerCase().replace(' ', '')}`, label)}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                <Divider sx={{ mb: 3 }} />

                {success ? renderSuccessContent() : renderStepContent(activeStep)}

                {renderFormActions()}
            </Box>
        </Box>
    );

    if (mode === 'modal') {
        return (
            <Modal open={open} onClose={onClose}>
                <Paper sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: isMobile ? '90%' : '500px',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 3,
                    outline: 'none',
                    maxHeight: '90vh',
                    overflowY: 'auto'
                }}>
                    {registerContent}
                </Paper>
            </Modal>
        );
    }

    return (
        <Container maxWidth="sm" sx={{ py: 4 }}>
            <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                {registerContent}
            </Paper>
        </Container>
    );
};

export default RegisterSystem;